import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthService, RegisterData, LoginResponse } from '@/services/auth/authService';
import { ApiError } from '@/types/carAuctionClient';
import { resetNativeAuthClientState } from '@/integrations/clients/carAuction/carAuctionClient';
import { useContext } from 'react';
import { AuthContext } from '@/context/AuthContext';
import { User, UserType } from '@/types/user';
import { clearNativeTokens, isNativeCapacitorApp } from '@/integrations/clients/carAuction/nativeAuth';
export const useAuth = () => {
  const queryClient = useQueryClient();
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  const {
    user,
    login: contextLogin,
    logout: contextLogout,
    updateUser,
    isLoading,
    isNativeSessionPending,
  } = authContext;

  const registerMutation = useMutation({
    mutationFn: (userData: RegisterData) => AuthService.register(userData),
    onError: (error: ApiError) => {},
  });

  const loginMutation = useMutation({
    mutationFn: (credentials: {
      email: string;
      password: string;
      isTest: boolean;
      emailVerified?: boolean;
    }) => AuthService.login(credentials),
    onSuccess: (loginResponse: LoginResponse) => {
      resetNativeAuthClientState();
      contextLogin(loginResponse.user, loginResponse.companyId);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      if (isNativeCapacitorApp()) {
        const { unregisterNativePushToken } = await import(
          '@/lib/native/pushNotifications'
        );
        await unregisterNativePushToken().catch(() => undefined);
      }
      return AuthService.logout();
    },
    onSuccess: () => {},
    onError: (error: ApiError) => {},
    onSettled: () => {
      clearNativeTokens();
      contextLogout();
      queryClient.clear();
    },
  });

  // Forgot password
  const requestPasswordResetMutation = useMutation({
    mutationFn: (email: string) => AuthService.requestPasswordReset(email),
  });

  // Email verification helpers
  const resendVerificationMutation = useMutation({
    mutationFn: (email: string) => AuthService.resendVerificationEmail(email),
  });

  const checkVerifiedMutation = useMutation({
    mutationFn: (email: string) => AuthService.checkEmailVerified(email),
    onSuccess: (verified: boolean) => {
      if (verified) {
        updateUser({ emailVerified: true });
      }
    },
  });

  const verifyEmailMutation = useMutation({
    mutationFn: (token: string) => AuthService.verifyEmail(token),
    onSuccess: () => {
      updateUser({ emailVerified: true });
    },
  });

  // Validate reset token
  const validateResetTokenMutation = useMutation({
    mutationFn: (token: string) => AuthService.validateResetToken(token),
  });

  // Reset password with token
  const resetPasswordMutation = useMutation({
    mutationFn: (payload: {
      token: string;
      password: string;
      email?: string;
    }) => AuthService.resetPassword(payload),
  });

  const updatePasswordMutation = useMutation({
    mutationFn: (payload: { currentPassword: string; newPassword: string }) =>
      AuthService.updatePassword(payload.currentPassword, payload.newPassword),
  });

  const deleteAccountMutation = useMutation({
    mutationFn: (payload: { reason: string; customReason?: string }) =>
      AuthService.deleteAccount(payload.reason, payload.customReason),
    onSuccess: () => {
      // Clear session storage and logout user
      sessionStorage.removeItem("userAuthToken");
      sessionStorage.removeItem("user");
      contextLogout();
      queryClient.clear();
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (payload: {
      userId: string;
      reason: string;
      customReason?: string;
    }) => AuthService.deleteUser(payload),
    onSuccess: () => {
      // Invalidate user-related queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });

  const acceptTermsMutation = useMutation({
    mutationFn: () => AuthService.acceptTerms(),
    onSuccess: () => {
      updateUser({ accountTermsAccepted: true });
    },
  });

  const acceptOtherTermsMutation = useMutation({
    mutationFn: () => AuthService.acceptOtherTerms(),
    onSuccess: () => {
      updateUser({ otherTermsAccepted: true });
    },
  });

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    isNativeSessionPending,

    register: registerMutation.mutate,
    registerAsync: registerMutation.mutateAsync,
    isRegistering: registerMutation.isPending,
    registerError: registerMutation.error,
    registerSuccess: registerMutation.isSuccess,

    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    loginSuccess: loginMutation.isSuccess,

    logout: logoutMutation.mutate,
    logoutAsync: logoutMutation.mutateAsync,
    isLoggingOut: logoutMutation.isPending,
    logoutError: logoutMutation.error,

    resetRegister: registerMutation.reset,
    resetLogin: loginMutation.reset,

    requestPasswordReset: requestPasswordResetMutation.mutate,
    requestPasswordResetAsync: requestPasswordResetMutation.mutateAsync,
    isRequestingPasswordReset: requestPasswordResetMutation.isPending,

    resendVerification: resendVerificationMutation.mutate,
    resendVerificationAsync: resendVerificationMutation.mutateAsync,
    isResendingVerification: resendVerificationMutation.isPending,
    checkEmailVerified: checkVerifiedMutation.mutate,
    checkEmailVerifiedAsync: checkVerifiedMutation.mutateAsync,
    isCheckingEmailVerified: checkVerifiedMutation.isPending,

    verifyEmail: verifyEmailMutation.mutate,
    verifyEmailAsync: verifyEmailMutation.mutateAsync,
    isVerifyingEmail: verifyEmailMutation.isPending,

    validateResetToken: validateResetTokenMutation.mutate,
    validateResetTokenAsync: validateResetTokenMutation.mutateAsync,
    isValidatingResetToken: validateResetTokenMutation.isPending,

    resetPassword: resetPasswordMutation.mutate,
    resetPasswordAsync: resetPasswordMutation.mutateAsync,
    isResettingPassword: resetPasswordMutation.isPending,

    updatePassword: updatePasswordMutation.mutate,
    updatePasswordAsync: updatePasswordMutation.mutateAsync,
    isUpdatingPassword: updatePasswordMutation.isPending,

    // Delete account
    deleteAccount: deleteAccountMutation.mutate,
    deleteAccountAsync: deleteAccountMutation.mutateAsync,
    isDeletingAccount: deleteAccountMutation.isPending,
    deleteAccountError: deleteAccountMutation.error,

    // Delete user (admin function)
    deleteUser: deleteUserMutation.mutate,
    deleteUserAsync: deleteUserMutation.mutateAsync,
    isDeletingUser: deleteUserMutation.isPending,
    deleteUserError: deleteUserMutation.error,

    // Accept terms
    acceptTerms: acceptTermsMutation.mutate,
    acceptTermsAsync: () => acceptTermsMutation.mutateAsync(),
    isAcceptingTerms: acceptTermsMutation.isPending,
    acceptTermsError: acceptTermsMutation.error,

    // Accept other terms (seller, auction, buyer, pricing)
    acceptOtherTerms: acceptOtherTermsMutation.mutate,
    acceptOtherTermsAsync: () => acceptOtherTermsMutation.mutateAsync(),
    isAcceptingOtherTerms: acceptOtherTermsMutation.isPending,
    acceptOtherTermsError: acceptOtherTermsMutation.error,
  };
};
